import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  Button,
  PageHeader,
  Select,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
  VerdictBadge,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, personLabel } from "@/lib/format";

type Status = "pending" | "accepted" | "rejected" | "all";

/**
 * Somebody says a fact about a cafe is wrong.
 *
 * Accepting writes an operator claim — source `admin`, weight 99, which
 * outranks everything else — against that fact. It does NOT write the
 * verdict: `cafe_fact_state` is the rollup worker's output, and a second
 * writer is how a read model starts disagreeing with the claims underneath
 * it. So the row says so on screen, and Ops has a button to run the
 * reconcile now rather than waiting for tonight.
 */
export function CorrectionsScreen() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>("pending");

  const corrections = useQuery({
    queryKey: ["admin", "corrections", status],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/corrections", {
        params: { query: { status, limit: 50 } },
      });
      if (error) throw error;
      return data;
    },
  });

  const resolve = useMutation({
    mutationFn: async (args: {
      correctionId: string;
      outcome: "accepted" | "rejected";
    }) => {
      const { error } = await api.POST(
        "/admin/corrections/{correctionId}/resolve",
        {
          params: { path: { correctionId: args.correctionId } },
          body: { outcome: args.outcome },
        },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin", "corrections"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "reports", "counts"],
      });
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Queues"
        title="Fact corrections"
        description="Accepting records an operator claim that outweighs everything else. The verdict itself moves on the next rollup — run it from Ops to see it now."
      />
      <ListScaffold
        filters={
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
          >
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </Select>
        }
        isLoading={corrections.isLoading}
        isError={corrections.isError}
        onRetry={() => void corrections.refetch()}
        isEmpty={(corrections.data?.items.length ?? 0) === 0}
        emptyTitle={status === "pending" ? "Nothing disputed" : "Nothing here"}
        emptyHint={
          status === "pending"
            ? "People raise these from a cafe page when a fact has gone stale or wrong."
            : undefined
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>Cafe</TH>
                <TH>Fact</TH>
                <TH>Says now</TH>
                <TH>Should be</TH>
                <TH>Because</TH>
                <TH>By</TH>
                <TH align="right" />
              </TR>
            </THead>
            <tbody>
              {corrections.data?.items.map((correction) => (
                <TR key={correction.id}>
                  <TD>
                    {correction.cafe ? (
                      <Link
                        to={`/cafes/${correction.cafe.id}`}
                        className="font-medium text-ink hover:text-accent hover:underline"
                      >
                        {correction.cafe.name}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TD>
                  <TD muted>{correction.label}</TD>
                  <TD>
                    <span className="flex items-center gap-1.5">
                      <span className="font-mono text-[11.5px] text-ink-2">
                        {correction.currentValue ?? "—"}
                      </span>
                      <VerdictBadge verdict={correction.currentVerdict} />
                    </span>
                  </TD>
                  <TD mono className="text-ink">
                    {correction.claim}
                  </TD>
                  <TD muted className="max-w-[240px]">
                    <span className="block truncate">
                      {correction.note ?? "—"}
                    </span>
                  </TD>
                  <TD>
                    <span className="block">
                      {personLabel(correction.user)}
                    </span>
                    <span className="text-micro text-muted">
                      {ago(correction.createdAt)}
                    </span>
                  </TD>
                  <TD align="right">
                    {correction.status === "pending" ? (
                      <span className="flex justify-end gap-1.5">
                        <Button
                          variant="primary"
                          disabled={resolve.isPending}
                          onClick={() =>
                            resolve.mutate({
                              correctionId: correction.id,
                              outcome: "accepted",
                            })
                          }
                        >
                          They are right
                        </Button>
                        <Button
                          variant="danger"
                          disabled={resolve.isPending}
                          onClick={() =>
                            resolve.mutate({
                              correctionId: correction.id,
                              outcome: "rejected",
                            })
                          }
                        >
                          Reject
                        </Button>
                      </span>
                    ) : (
                      <Badge
                        tone={
                          correction.status === "accepted"
                            ? "success"
                            : "neutral"
                        }
                      >
                        {correction.status}
                      </Badge>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
